import { useState } from "react";
import {
  CaretDown,
  ChartLine, ChartLineUp, ChartBar, Columns, ChartDonut, Stack, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { TooltipProvider } from "../../ui/tooltip";
import { SERIES } from "../../../lib/chartSeries";
import { OptChip, ChipGroup } from "./OptChip";

const CHART_TYPES = [
  { v: "line", label: "Line", Icon: ChartLine },
  { v: "area", label: "Area", Icon: ChartLineUp },
  { v: "stacked", label: "Stacked bar", Icon: Stack },
  { v: "grouped", label: "Grouped bar", Icon: Columns },
  { v: "combo", label: "Combo", Icon: ChartBar },
  { v: "donut", label: "Donut", Icon: ChartDonut },
];
const AXES = [
  { v: "monthly", label: "Monthly" }, { v: "loan_year", label: "Loan year" },
  { v: "cy", label: "Calendar year" }, { v: "fy", label: "Financial year (Apr–Mar)" },
  { v: "custom", label: "Custom range" },
];
const MODES = [
  { v: "individual", label: "Individual" },
  { v: "combined", label: "Cumulative - combined" },
  { v: "split", label: "Cumulative - split by loan" },
];

function Swatch({ color, dash }) {
  return (
    <svg width="16" height="6" aria-hidden="true">
      <line x1="0" y1="3" x2="16" y2="3" stroke={color} strokeWidth="2.5" strokeDasharray={dash || undefined} />
    </svg>
  );
}

export function ChartSettingsBar({ s, set, mode, onMode, loans, loanIdx, onLoanIdx, reasons, isDefault, onReset }) {
  const chips = (key, options, group) => options.map((o) => (
    <OptChip key={o.v} active={s[key] === o.v} reason={group?.[o.v] || (["timeAxis", "window"].includes(key) ? reasons.time : null)}
             onClick={() => set({ [key]: o.v })} testid={`chart-${key}-${o.v}`}>
      {o.Icon && <o.Icon size={12} weight="duotone" />}{o.label}
    </OptChip>
  ));
  const [open, setOpen] = useState(() => { try { return window.innerWidth >= 768; } catch { return true; } });
  return (
    <TooltipProvider delayDuration={150}>
      <div className="card-flat p-4 space-y-3" data-testid="chart-settings-bar">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
                className="md:hidden w-full flex items-center justify-between text-xs font-semibold" data-testid="chart-settings-toggle">
          Chart settings
          <CaretDown size={12} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        <div className={`${open ? "grid" : "hidden"} md:grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3`}>
          <ChipGroup label="Loans" testid="chart-group-loans">
            {MODES.map((m) => (
              <OptChip key={m.v} active={mode === m.v} reason={reasons.loans[m.v]} onClick={() => onMode(m.v)} testid={`chart-mode-${m.v}`}>
                {m.label}
              </OptChip>
            ))}
            {mode === "individual" && (
              <select value={loanIdx} onChange={(e) => onLoanIdx(Number(e.target.value))}
                      className="input-dark px-2 py-1 text-[11px]" data-testid="chart-select-loan">
                {loans.map((l, i) => <option key={l._k || l.name} value={i}>{l.name}</option>)}
              </select>
            )}
          </ChipGroup>
          <ChipGroup label="Chart type" testid="chart-group-type">{chips("chartType", CHART_TYPES, reasons.chartType)}</ChipGroup>
          <ChipGroup label="Measure" testid="chart-group-measure">
            {chips("measure", [{ v: "per_period", label: "Per period" }, { v: "running", label: "Running total" }], reasons.measure)}
          </ChipGroup>
          <ChipGroup label="Series to show" testid="chart-group-series">
            {SERIES.map((x) => (
              <OptChip key={x.key} active={s.series[x.key] || (x.key === "balance" && s.chartType === "combo" && !s.split)}
                       reason={reasons.series[x.key]} testid={`chart-series-${x.key}`}
                       onClick={() => set({ series: { ...s.series, [x.key]: !s.series[x.key] } })}>
                <Swatch color={x.color} dash={x.dash} />{x.label}
              </OptChip>
            ))}
            {s.split && (
              <span className="inline-flex gap-1 ml-1" data-testid="chart-split-metric">
                {chips("splitMetric", [{ v: "principal", label: "Principal" }, { v: "interest", label: "Interest" }])}
              </span>
            )}
          </ChipGroup>
          <ChipGroup label="Time axis" testid="chart-group-axis">
            {chips("timeAxis", AXES)}
            {s.timeAxis === "custom" && !reasons.time && (
              <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <input type="month" value={s.rangeFrom} onChange={(e) => set({ rangeFrom: e.target.value })}
                       className="input-dark px-1.5 py-0.5 text-[11px]" data-testid="chart-range-from" aria-label="From month" />
                to
                <input type="month" value={s.rangeTo} onChange={(e) => set({ rangeTo: e.target.value })}
                       className="input-dark px-1.5 py-0.5 text-[11px]" data-testid="chart-range-to" aria-label="To month" />
              </span>
            )}
          </ChipGroup>
          <ChipGroup label="Time window" testid="chart-group-window">
            {chips("window", [{ v: "past", label: "Past only" }, { v: "future", label: "Future only" }, { v: "full", label: "Full life" }])}
          </ChipGroup>
          <ChipGroup label="Value display" testid="chart-group-display">
            {chips("display", [{ v: "rupees", label: "Rupees" }, { v: "pct", label: "% of EMI" }], reasons.display)}
          </ChipGroup>
          <ChipGroup label="Rate changes" testid="chart-group-rates">
            <OptChip active={!s.applyRateChanges} onClick={() => set({ applyRateChanges: false })} testid="chart-rates-markers">Markers only</OptChip>
            <OptChip active={s.applyRateChanges} onClick={() => set({ applyRateChanges: true })} testid="chart-rates-resplit">Re-split after each change</OptChip>
          </ChipGroup>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t hairline pt-2">
          <span className="text-[10px] text-muted-foreground" data-testid="chart-settings-status">
            {isDefault ? "Showing the standard graphs. Change any setting to build a custom chart." : "Custom chart — your choice is remembered."}
          </span>
          <button type="button" onClick={onReset} disabled={isDefault}
                  className="ml-auto btn-ghost px-2 py-1 text-[11px] inline-flex items-center gap-1 border hairline rounded disabled:opacity-40"
                  data-testid="chart-reset-default">
            <ArrowCounterClockwise size={11} /> Reset to default
          </button>
        </div>
      </div>
    </TooltipProvider>
  );
}
